import { Component, OnInit, HostListener, Input, EventEmitter, Output } from '@angular/core';
import { ConfigService } from '@services/config.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-file-upload',
  templateUrl: './fileupload.component.html',
  styleUrls: ['./fileupload.component.css'],
})
export class FileUploadComponent implements OnInit {
  dragAreaClass = 'dragarea';

  errors: FileStatus[] = [];
  success: FileStatus[] = [];

  @Input() fileExt: string;
  @Input() maxFiles = 0;
  @Input() maxSize = 0;
  @Output() uploadStatus = new EventEmitter();
  @Output() deleteFile = new EventEmitter();
  @Input() lastImportedDate = Date;

  constructor(private configService: ConfigService) {}

  async ngOnInit() {
    const results = await firstValueFrom(
      this.configService.getConfigValuesFromSection(
        [ConfigService.DEFAULT_MAX_FILELENGTH, ConfigService.DEFAULT_MAX_FILESIZE_IN_MB],
        ConfigService.UPLOADFILESETTING_SECTION
      )
    );
    if (this.maxFiles === 0) {
      this.maxFiles = results[ConfigService.DEFAULT_MAX_FILELENGTH] ? results[ConfigService.DEFAULT_MAX_FILELENGTH] : 1;
    }

    if (this.maxSize === 0) {
      this.maxSize = results[ConfigService.DEFAULT_MAX_FILESIZE_IN_MB] ? results[ConfigService.DEFAULT_MAX_FILESIZE_IN_MB] : 5;
    }
  }

  @HostListener('dragover', ['$event']) onDragOver(event) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }

  @HostListener('dragenter', ['$event']) onDragEnter(event) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }

  @HostListener('dragend', ['$event']) onDragEnd(event) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }

  @HostListener('dragleave', ['$event']) onDragLeave(event) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }
  @HostListener('drop', ['$event']) onDrop(event) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    this.uploadFiles(files);
  }

  onFileChange(event) {
    const files = event.target.files;
    this.uploadFiles(files);
  }

  uploadFiles(files) {
    this.errors = []; // Clear error
    this.success = []; // Clear Success

    // Validate file size and allowed extensions
    if (files.length > 0 && !this.isValidFiles(files)) {
      this.uploadStatus.emit(null);
      return;
    } else {
      for (let i = 0; i < files.length; i++) {
        this.success.push({ name: files[i].name, status: '' });
      }
    }

    if (files.length > 0) {
      const formData: FormData = new FormData();
      for (let j = 0; j < files.length; j++) {
        formData.append('file[]', files[j], files[j].name);
      }
      this.uploadStatus.emit(files);
    }
  }

  private isValidFiles(files) {
    // Check Number of files
    if (files.length > this.maxFiles) {
      this.errors.push({ name: '', status: 'At a time you can upload only ' + this.maxFiles + ' files' });
      return false;
    }
    this.isValidFileExtension(files);
    return this.errors.length === 0;
  }

  private isValidFileExtension(files) {
    // Make array of file extensions
    const extensions = this.fileExt.split(',').map(function (x) {
      return x.toLocaleUpperCase().trim();
    });

    for (let i = 0; i < files.length; i++) {
      // Get file extension
      const ext = files[i].name.toUpperCase().split('.').pop() || files[i].name;
      // Check the extension exists
      const exists = extensions.includes(ext);
      if (!exists) {
        this.errors.push({ name: files[i].name, status: 'This file is not a supported file type.' });
      }
      // Check file size
      this.isValidFileSize(files[i]);
    }
  }

  private isValidFileSize(file) {
    const fileSizeinMB = file.size / (1024 * 1000);
    const size = Math.round(fileSizeinMB * 100) / 100; // convert upto 2 decimal place
    if (size > this.maxSize) {
      this.errors.push({ name: file.name, status: 'exceeds file size limit of ' + this.maxSize + 'MB ( ' + size + 'MB )' });
    }
  }

  deleteScreenshot(fileName: string) {
    if (this.success.length > 0) {
      this.deleteFile.emit(this.success.find((f) => f.name === fileName));
      this.success = this.success.filter((f) => f.name !== fileName);
    } else if (this.errors.length > 0) {
      this.errors = this.errors.filter((f) => f.name !== fileName);
    }
  }
}

export class FileStatus {
  name: string;
  status: string;
}
